/**
 * Borrower Intake Wizard
 *
 * Pre-deal questionnaire flow for borrowers before submitting loan applications.
 * Collects business type, eligibility information, and communication preferences.
 * Data is saved to localStorage between sessions until final submission.
 *
 * Issue #141 - CRM/Lead Path Frontend - Borrower Intake Wizard
 */

import BackIcon from '@mui/icons-material/ArrowBack';
import NextIcon from '@mui/icons-material/ArrowForward';
import CheckIcon from '@mui/icons-material/Check';
import {
  Box,
  GlassAlert,
  GlassButton,
  GlassCard,
  Step,
  StepLabel,
  Stepper,
  Typography,
  useI18n,
} from '@ops-matrix/design-system-ui';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageTemplate } from '../../components/layout/PageTemplate';
import BusinessTypeStep from './steps/BusinessTypeStep';
import EligibilityStep from './steps/EligibilityStep';
import PreferencesStep from './steps/PreferencesStep';
import ReviewStep from './steps/ReviewStep';

const STORAGE_KEY = 'opsmatrix_intake_wizard_data';

export interface IntakeFormData {
  // Business Type (Step 1)
  businessType: 'individual' | 'llc' | 'corporation' | 'trust' | 'partnership' | '';
  businessName: string;
  yearsInBusiness: string;
  stateOfFormation: string;

  // Eligibility (Step 2)
  loanPurpose: 'purchase' | 'refinance' | 'cash_out' | 'bridge' | '';
  propertyType: 'single_family' | 'multi_family' | 'commercial' | 'mixed_use' | '';
  estimatedLoanAmount: string;
  creditScoreRange: 'excellent' | 'good' | 'fair' | 'poor' | '';
  hasExperience: boolean;
  numberOfProperties: string;

  // Preferences (Step 3)
  preferredContact: 'email' | 'phone' | 'sms' | '';
  timezone: string;
  bestTimeToContact: 'morning' | 'afternoon' | 'evening' | '';
  marketingOptIn: boolean;
}

const INITIAL_DATA: IntakeFormData = {
  businessType: '',
  businessName: '',
  yearsInBusiness: '',
  stateOfFormation: '',
  loanPurpose: '',
  propertyType: '',
  estimatedLoanAmount: '',
  creditScoreRange: '',
  hasExperience: false,
  numberOfProperties: '',
  preferredContact: '',
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  bestTimeToContact: '',
  marketingOptIn: false,
};

const STEP_KEYS = [
  'consumer.intake.step.businessType',
  'consumer.intake.step.eligibility',
  'consumer.intake.step.preferences',
  'consumer.intake.step.review',
] as const;

export default function IntakeWizard() {
  const navigate = useNavigate();
  const { t } = useI18n();

  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState<IntakeFormData>(INITIAL_DATA);
  const [stepErrors, setStepErrors] = useState<Record<number, string[]>>({});

  // Load saved data from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setFormData((prev) => ({ ...prev, ...parsed }));
      } catch {
        // Invalid data, start fresh
      }
    }
  }, []);

  // Save to localStorage whenever formData changes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(formData));
  }, [formData]);

  const updateFormData = useCallback((updates: Partial<IntakeFormData>) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  }, []);

  const validateStep = useCallback(
    (step: number): string[] => {
      const errors: string[] = [];

      switch (step) {
        case 0: // Business Type
          if (!formData.businessType) {
            errors.push(t('consumer.intake.validation.selectBusinessType'));
          }
          break;
        case 1: // Eligibility
          if (!formData.loanPurpose) {
            errors.push(t('consumer.intake.validation.selectLoanPurpose'));
          }
          if (!formData.propertyType) {
            errors.push(t('consumer.intake.validation.selectPropertyType'));
          }
          break;
        case 2: // Preferences
          if (!formData.preferredContact) {
            errors.push(t('consumer.intake.validation.selectContactMethod'));
          }
          break;
      }

      return errors;
    },
    [formData],
  );

  const handleNext = useCallback(() => {
    const errors = validateStep(activeStep);
    if (errors.length > 0) {
      setStepErrors((prev) => ({ ...prev, [activeStep]: errors }));
      return;
    }

    setStepErrors((prev) => ({ ...prev, [activeStep]: [] }));
    setActiveStep((prev) => prev + 1);
  }, [activeStep, validateStep]);

  const handleBack = useCallback(() => {
    setActiveStep((prev) => prev - 1);
  }, []);

  const handleComplete = useCallback(() => {
    // Clear saved data
    localStorage.removeItem(STORAGE_KEY);

    // Navigate to dashboard after completing intake
    navigate('/dashboard');
  }, [formData, navigate]);

  const handleSkip = useCallback(() => {
    // Allow users to skip intake and go to dashboard
    navigate('/dashboard');
  }, [navigate]);

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return <BusinessTypeStep formData={formData} updateFormData={updateFormData} />;
      case 1:
        return <EligibilityStep formData={formData} updateFormData={updateFormData} />;
      case 2:
        return <PreferencesStep formData={formData} updateFormData={updateFormData} />;
      case 3:
        return <ReviewStep formData={formData} onEdit={setActiveStep} />;
      default:
        return null;
    }
  };

  const currentErrors = stepErrors[activeStep] || [];

  return (
    <PageTemplate variant="form" noPadding>
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
          {t('consumer.intake.title')}
        </Typography>
        <Typography color="textSecondary">{t('consumer.intake.subtitle')}</Typography>
      </Box>

      <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
        {STEP_KEYS.map((key) => (
          <Step key={key}>
            <StepLabel>{t(key)}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {currentErrors.length > 0 && (
        <GlassAlert severity="error" visible={true} sx={{ mb: 3 }}>
          {currentErrors.map((error, i) => (
            <div key={i}>{error}</div>
          ))}
        </GlassAlert>
      )}

      <GlassCard>{renderStepContent()}</GlassCard>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Box>
          {activeStep > 0 ? (
            <GlassButton variant="outlined" color="secondary" startIcon={<BackIcon />} onClick={handleBack}>
              {t('common.back')}
            </GlassButton>
          ) : (
            <GlassButton variant="ghost" color="secondary" onClick={handleSkip}>
              {t('consumer.intake.skipForNow')}
            </GlassButton>
          )}
        </Box>

        <Box>
          {activeStep < STEP_KEYS.length - 1 ? (
            <GlassButton variant="gradient" endIcon={<NextIcon />} onClick={handleNext}>
              {t('consumer.intake.continue')}
            </GlassButton>
          ) : (
            <GlassButton variant="gradient" endIcon={<CheckIcon />} onClick={handleComplete}>
              {t('consumer.intake.completeSubmit')}
            </GlassButton>
          )}
        </Box>
      </Box>
    </PageTemplate>
  );
}
